import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { formatDate } from '../../lib/formatUtils';
import { useAuth } from '../../context/AuthContext';
import { PersonAvatar } from '../../components/PersonAvatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Clock, CalendarDays, Wallet, FileText, LogIn, LogOut, ArrowRight, User, Briefcase, Loader2, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

export const MyPortalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [recentPayslips, setRecentPayslips] = useState([]);
  const [selfReview, setSelfReview] = useState({ cycle: null, data: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (searchParams.get('access_denied')) {
      toast.error('Access denied. You do not have permission to view that page.');
      searchParams.delete('access_denied');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    Promise.all([
      api.get('/my/profile').then(r => setProfile(r.data.data)).catch(() => {}),
      api.get('/attendance/today').then(r => setTodayAttendance(r.data.data)).catch(() => {}),
      api.get('/my/leaves?status=').then(r => setRecentLeaves(r.data.data?.slice(0, 3) || [])).catch(() => {}),
      api.get(`/my/payslips?year=${new Date().getFullYear()}`).then(r => setRecentPayslips(r.data.data?.slice(0, 3) || [])).catch(() => {}),
      api.get('/appraisals/self-review/my').then(r => setSelfReview({ cycle: r.data.cycle, data: r.data.data })).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

  const getStatusBadge = (status) => {
    const cfg = { approved: 'bg-emerald-500/10 text-emerald-500', pending: 'bg-amber-500/10 text-amber-500', rejected: 'bg-red-500/10 text-red-500', paid: 'bg-emerald-500/10 text-emerald-500' };
    return <Badge className={cfg[status] || cfg.pending}>{status}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-portal-page">
      {/* Welcome Header */}
      <div className="flex items-center gap-4">
        <PersonAvatar
          firstName={user?.first_name}
          lastName={user?.last_name}
          picture={user?.profile_picture || profile?.profile_picture}
          seed={user?.id}
          size="xl"
        />
        <div>
          <h1 className="text-2xl font-bold">Welcome back, {user?.first_name || 'Employee'}</h1>
          <p className="text-muted-foreground">
            {profile?.designation && <span>{profile.designation}</span>}
            {profile?.department && <span> | {profile.department}</span>}
          </p>
        </div>
      </div>

      {/* Self-Review CTA — visible only if employee is part of an active cycle */}
      {selfReview.cycle && (
        <Card
          className={`cursor-pointer transition-colors border-2 ${selfReview.data?.status === 'submitted' ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10' : 'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10'}`}
          onClick={() => navigate('/my-portal/self-review')}
          data-testid="portal-self-review-card"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${selfReview.data?.status === 'submitted' ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
              <ClipboardCheck className={`h-6 w-6 ${selfReview.data?.status === 'submitted' ? 'text-emerald-500' : 'text-amber-500'}`} />
            </div>
            <div className="flex-1">
              <p className="font-semibold">Annual Self-Review — {selfReview.cycle.fy}</p>
              <p className="text-sm text-muted-foreground">
                {selfReview.data?.status === 'submitted'
                  ? 'You\'ve submitted your self-review. Click to view it.'
                  : selfReview.data?.status === 'draft'
                    ? `Draft saved. Submit before ${formatDate(selfReview.cycle.deadline)}.`
                    : `Action required. Submit before ${formatDate(selfReview.cycle.deadline)}.`}
              </p>
            </div>
            {selfReview.data?.status === 'submitted'
              ? <Badge className="bg-emerald-500/10 text-emerald-500">Submitted</Badge>
              : selfReview.data?.status === 'draft'
                ? <Badge className="bg-amber-500/10 text-amber-500">Draft</Badge>
                : <Badge className="bg-red-500/10 text-red-500">Pending</Badge>}
            <ArrowRight className="h-5 w-5 text-muted-foreground" />
          </CardContent>
        </Card>
      )}

      {/* Quick Nav Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/my-portal/attendance')} data-testid="portal-nav-attendance">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><Clock className="h-5 w-5 text-blue-500" /></div><div><p className="font-semibold text-sm">My Attendance</p><p className="text-xs text-muted-foreground">View history</p></div></div></CardContent>
        </Card>
        <Card className="stat-card cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/my-portal/payslips')} data-testid="portal-nav-payslips">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-emerald-500" /></div><div><p className="font-semibold text-sm">My Payslips</p><p className="text-xs text-muted-foreground">Salary details</p></div></div></CardContent>
        </Card>
        <Card className="stat-card cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/my-portal/leave')} data-testid="portal-nav-leave">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-purple-500" /></div><div><p className="font-semibold text-sm">Apply Leave</p><p className="text-xs text-muted-foreground">Request time off</p></div></div></CardContent>
        </Card>
        <Card className="stat-card cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/my-portal/profile')} data-testid="portal-nav-profile">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><User className="h-5 w-5 text-amber-500" /></div><div><p className="font-semibold text-sm">My Profile</p><p className="text-xs text-muted-foreground">View details</p></div></div></CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Attendance */}
        <Card data-testid="portal-today-attendance">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-primary" />Today's Status</CardTitle>
          </CardHeader>
          <CardContent>
            {todayAttendance ? (
              <div className="space-y-3">
                <div className="flex items-center gap-4 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                  <LogIn className="h-5 w-5 text-emerald-500" />
                  <div className="flex-1"><p className="text-sm text-muted-foreground">Check In</p><p className="text-lg font-bold" data-testid="portal-checkin-time">{todayAttendance.check_in || '--:--'}</p></div>
                </div>
                <div className={`flex items-center gap-4 p-3 rounded-lg ${todayAttendance.check_out ? 'bg-blue-500/5 border border-blue-500/20' : 'bg-muted/20 border border-border'}`}>
                  <LogOut className={`h-5 w-5 ${todayAttendance.check_out ? 'text-blue-500' : 'text-muted-foreground'}`} />
                  <div className="flex-1"><p className="text-sm text-muted-foreground">Check Out</p><p className="text-lg font-bold" data-testid="portal-checkout-time">{todayAttendance.check_out || 'In Progress'}</p></div>
                  {todayAttendance.total_hours != null && <Badge className="bg-blue-500/10 text-blue-500">{todayAttendance.total_hours}h</Badge>}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No attendance recorded today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Leaves */}
        <Card data-testid="portal-recent-leaves">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><CalendarDays className="h-5 w-5 text-purple-500" />Recent Leave Requests</CardTitle>
              <button onClick={() => navigate('/my-portal/leave')} className="text-xs text-primary hover:underline flex items-center gap-1">View All <ArrowRight className="h-3 w-3" /></button>
            </div>
          </CardHeader>
          <CardContent>
            {recentLeaves.length > 0 ? (
              <div className="space-y-2">
                {recentLeaves.map(l => (
                  <div key={l.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20" data-testid={`portal-leave-${l.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{l.type} Leave{l.is_half_day ? ' (Half Day)' : ''}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(l.start_date)}{l.start_date !== l.end_date ? ` to ${formatDate(l.end_date)}` : ''} | {l.days} day{l.days !== 1 ? 's' : ''}</p>
                    </div>
                    {getStatusBadge(l.status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No leave requests yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/my-portal/leave')}>Apply Now</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Payslips */}
      {recentPayslips.length > 0 && (
        <Card data-testid="portal-recent-payslips">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><Wallet className="h-5 w-5 text-emerald-500" />Recent Payslips</CardTitle>
              <button onClick={() => navigate('/my-portal/payslips')} className="text-xs text-primary hover:underline flex items-center gap-1">View All <ArrowRight className="h-3 w-3" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentPayslips.map(p => (
                <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20" data-testid={`portal-payslip-${p.id}`}>
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-emerald-500" /></div>
                  <div className="flex-1"><p className="text-sm font-medium">{p.month} {p.year}</p><p className="text-xs text-muted-foreground">Net: {formatCurrency(p.net_salary)}</p></div>
                  {getStatusBadge(p.status)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
